/**
 * What you have, in four numbers.
 *
 * Home was deliberately not a dashboard, and it should stay not-a-dashboard —
 * counters competing with the photos themselves is the failure mode. But there
 * is a real difference between a widget wall and four facts a person actually
 * wonders about: how much is in here, how much room is left, how many trips,
 * and where they were. That last one is the only sentimental number in the
 * product and it costs nothing to show.
 *
 * Desktop only. On a phone this space is worth more as albums.
 */

import { lazy, Suspense, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { formatBytes } from "@fc/core";
import { errorMessage, trpc } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { codeFromFlag, countryName, flagFor } from "../../lib/countries.js";
import { useLocale } from "../../lib/i18n.js";
import type { HomeAlbum } from "./AlbumRow.js";

/*
 * Loaded on demand.
 *
 * The world is about 140 KB of path data — a tenth of the whole bundle for
 * something only the home screen draws, and only on a wide one. Splitting it
 * keeps it out of the door on a phone, and out of the way of the first paint
 * everywhere else.
 */
const WorldMap = lazy(() =>
  import("../map/WorldMap.js").then((m) => ({ default: m.WorldMap })),
);

interface HomeStatsProps {
  /** Already loaded for the album row; reused so this costs no extra request. */
  albums: HomeAlbum[];
  groups: { id: string; name: string; emoji: string | null }[];
}

export function HomeStats({ albums, groups }: HomeStatsProps) {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const stats = useQuery(trpc.groups.stats.queryOptions());
  const map = useQuery(trpc.groups.countries.queryOptions());
  const queryClient = useQueryClient();
  const [country, setCountry] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  /** Shown when the map refuses a change, rather than nothing happening. */
  const [mapError, setMapError] = useState<string | null>(null);

  const setCountryOnMap = useMutation(
    trpc.groups.setCountry.mutationOptions({
      /*
       * Applied here first, then confirmed.
       *
       * Pressing a country used to change nothing until a mutation, an
       * invalidation and a refetch had all completed — which reads as a
       * button that does not work, and reads exactly the same way when the
       * request has genuinely failed. The map now moves at once and reverts
       * if the server disagrees, with the reason said out loud.
       */
      onMutate: ({ countryCode, mode }) => {
        setMapError(null);
        const key = trpc.groups.countries.queryKey();
        const previous = queryClient.getQueryData(key);

        queryClient.setQueryData(key, (current) => {
          if (!current) return current;
          const countries = current.countries.filter(
            (country) => country.code !== countryCode,
          );
          const hidden = current.hidden.filter((code) => code !== countryCode);

          if (mode === "added") {
            countries.push({ code: countryCode, albums: 0, manual: true });
            countries.sort((a, b) => a.code.localeCompare(b.code));
          } else if (mode === "hidden") {
            hidden.push(countryCode);
            hidden.sort();
          }
          /* `null` forgets the correction; the refetch restores the albums. */

          return { ...current, countries, hidden };
        });

        return { previous };
      },
      onError: (error, _input, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            trpc.groups.countries.queryKey(),
            context.previous,
          );
        }
        setMapError(errorMessage(error));
      },
      onSettled: () =>
        /*
         * Both, not just the map: the "Countries" figure beside it is derived
         * from the same albums, and a number that disagrees with the map next
         * to it is worse than a slow number.
         */
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.groups.countries.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.groups.stats.queryKey(),
          }),
        ]),
    }),
  );

  /*
   * Everything filed under the country whose flag it carries, so pressing one
   * can offer a choice rather than guessing which of four albums was meant.
   */
  const byCountry = useMemo(() => {
    const index = new Map<
      string,
      { albums: HomeAlbum[]; groups: typeof groups }
    >();

    const bucket = (code: string) => {
      const existing = index.get(code);
      if (existing) return existing;
      const created = {
        albums: [] as HomeAlbum[],
        groups: [] as typeof groups,
      };
      index.set(code, created);
      return created;
    };

    for (const album of albums) {
      /*
       * The chosen country wins. The flag is a fallback for albums made
       * before the field existed — and for one made since where somebody
       * picked a Spanish flag and named the country Portugal, the answer
       * they gave deliberately is the one to believe.
       */
      /* An album can name several now, so it appears under each of them. */
      const chosen = album.countryCodes.map((c) => c.toUpperCase());
      const codes = chosen.length > 0 ? chosen : [codeFromFlag(album.emoji)];
      for (const code of codes) if (code) bucket(code).albums.push(album);
    }
    for (const group of groups) {
      const code = codeFromFlag(group.emoji);
      if (code) bucket(code).groups.push(group);
    }

    return index;
  }, [albums, groups]);

  if (!stats.data) return null;

  const used = Number(stats.data.usedBytes);
  const limit = Number(stats.data.limitBytes);
  const left = Math.max(0, limit - used);
  const codes = stats.data.countries;

  const chosen = country ? byCountry.get(country) : undefined;

  /**
   * Show what is in a country.
   *
   * One match opens straight away — a chooser listing a single option is a
   * dialog asking somebody to confirm their own click. Shared by the flag row
   * and the map, so pressing Croatia means the same thing in both.
   */
  const openCountry = (code: string) => {
    const entry = byCountry.get(code);
    const single =
      entry && entry.albums.length + entry.groups.length === 1
        ? (entry.albums[0] ?? entry.groups[0])
        : null;
    if (single) open(single);
    else setCountry(code);
  };

  const open = (target: { id: string; groupId?: string }) => {
    setCountry(null);
    void navigate({
      to: "/g/$groupId",
      params: { groupId: target.groupId ?? target.id },
      search: target.groupId ? { folder: target.id } : {},
    });
  };

  return (
    <section className="fc-stats" aria-label={t("Your library")}>
      <dl className="fc-stats__grid">
        <Stat
          label={t("Photos and videos")}
          value={stats.data.files.toLocaleString()}
        />
        <Stat
          label={t("Storage left")}
          value={formatBytes(left)}
          /* The total is the context that makes "12 GB left" mean anything. */
          note={limit > 0 ? `${t("of")} ${formatBytes(limit)}` : undefined}
        />
        <Stat label={t("Trips")} value={stats.data.trips.toLocaleString()} />
        <Stat
          label={t("Countries")}
          value={(map.data?.countries.length ?? codes.length).toLocaleString()}
        />
      </dl>

      {codes.length > 0 && (
        <ul className="fc-stats__flags" role="list">
          {codes.map((code) => (
            <li key={code}>
              <button
                type="button"
                className="fc-stats__flag"
                title={countryName(code, locale)}
                onClick={() => openCountry(code)}
              >
                <EmojiIcon
                  emoji={flagFor(code)}
                  label={countryName(code, locale)}
                />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/*
        The map, under the flags rather than instead of them.

        The flag row is a set of shortcuts — press one and it opens the album.
        The map answers a different question, which is "where have we not been
        yet", and a row of flags cannot answer that at all: you would have to
        know the list of countries you have not been to in order to notice one
        missing from it.
      */}
      {map.data && (
        <div className="fc-stats__map">
          <div className="fc-stats__map-head">
            <h3>{t("Where you have been")}</h3>
            <button
              type="button"
              className="fc-stats__edit"
              aria-pressed={editing}
              onClick={() => setEditing((on) => !on)}
            >
              {editing ? t("Done") : t("Edit map")}
            </button>
          </div>

          <Suspense
            fallback={
              <div className="fc-stats__map-loading" aria-hidden="true" />
            }
          >
            <WorldMap
              /*
               * The album titles travel with the counts, so hovering a country
               * can name the trips rather than only counting them. Read from
               * the index already built above for the flag row, so this costs
               * nothing extra.
               */
              countries={map.data.countries.map((country) => ({
                ...country,
                albumNames: (byCountry.get(country.code)?.albums ?? []).map(
                  (album) => album.name,
                ),
              }))}
              hidden={map.data.hidden}
              editable={editing}
              /*
               * From the map, always the list.
               *
               * The flag row opens a single match straight away, because a
               * flag is a shortcut to a place you already have in mind. A
               * country on the map is a question — "what have we got from
               * there" — and answering it by navigating away, before the
               * albums have been seen, answers a different one.
               */
              onOpen={(code) => setCountry(code)}
              onToggle={(code, next) => {
                setCountryOnMap.mutate({ countryCode: code, mode: next });
              }}
            />
          </Suspense>

          {/* A refused change used to look exactly like a button doing
              nothing. */}
          {mapError && (
            <p className="fc-admin__error" role="alert">
              {mapError}
            </p>
          )}
        </div>
      )}

      {country && (
        <Dialog
          open
          onClose={() => setCountry(null)}
          title={countryName(country, locale)}
          description={t("Open one of these.")}
        >
          <ul className="fc-stats__choices" role="list">
            {chosen?.albums.map((album) => (
              <li key={album.id}>
                <button type="button" onClick={() => open(album)}>
                  <EmojiIcon emoji={album.emoji} />
                  <span>{album.name}</span>
                  <span className="fc-stats__choice-note">
                    {album.groupName}
                  </span>
                </button>
              </li>
            ))}
            {chosen?.groups.map((group) => (
              <li key={group.id}>
                <button type="button" onClick={() => open({ id: group.id })}>
                  <EmojiIcon emoji={group.emoji} />
                  <span>{group.name}</span>
                  <span className="fc-stats__choice-note">{t("Group")}</span>
                </button>
              </li>
            ))}
          </ul>
        </Dialog>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="fc-stats__stat">
      <dt>{label}</dt>
      <dd>
        <span className="fc-stats__value fc-tabular">{value}</span>
        {note && <span className="fc-stats__note">{note}</span>}
      </dd>
    </div>
  );
}
